import React, { type FC } from "react";
import { type StoryListItemProps } from "./StoryListItem";
import { Stack, Heading, Flex, Text as ChakraText, Box } from "@chakra-ui/react";
import { sc } from "../../_Base/Jss";
import { type SearchResultTopicModel } from "~/feature/Search/generated-types";
import TagLink from "../../_Base/Components/Tags/TagLink.tsx";
import { dateFormatter } from "~/foundation/Utils/dateFormatter";
import { useSitecoreContext } from "~/foundation/Jss";
import { Text as ThemeTextStyling } from "../../_Base/Theme/components/Text";

const StoryListItemText: FC<StoryListItemProps> = ({
	title,
	tags,
	description,
	eventEndDate,
	eventCountry,
	date,
}) => {
	const { sitecoreContext } = useSitecoreContext();
	const isRtl = sitecoreContext.custom.settings.isRtl;

	const isValidDate = (dateValue: string | Date | undefined): boolean => {
		if (!dateValue) return false;
		const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
		return date.getFullYear() > 1900;
	};

	// They format countries and states like this "country name;state name"
	const getCountryLabel = (countryString: string | undefined): string => {
		if (!countryString) return '';
		const country = countryString as string;
		return country.split(';')[0].trim();
	};

	return (
		<Stack p="0" w="100%">
			{title &&
				<Heading size={["mobileHeadline7", null, "desktopHeadline5"]} my={0}>
					{title}
				</Heading>
			}

			{tags && tags?.length > 0 && (
				<Flex flexWrap="wrap" gap={3}>
					{tags?.map((tagItem: SearchResultTopicModel, i) => (
						<React.Fragment key={i}>
							{tagItem.id ? (
								<TagLink tag={tagItem}>
									<ChakraText as="span" variant="tag">
										{tagItem.title}
									</ChakraText>
								</TagLink>
							) : (
								<ChakraText variant="tag">
									{tagItem.title}
								</ChakraText>
							)}
						</React.Fragment>
					))}
				</Flex>
			)}

			{/*
					Eventdate and country is only on events, no need to check for content type
					Events don't have tags, so won't stack with the above.
				*/}
			{eventCountry && (
				<ChakraText variant="tag">
					{getCountryLabel(eventCountry)}
				</ChakraText>
			)}

			{(eventEndDate || date) && (
				<ChakraText mt={3}>
					<time>
						{isValidDate(date) && dateFormatter(typeof date === 'string' ? new Date(date) : date!, isRtl)}
						{isValidDate(eventEndDate) && isValidDate(date) && " - "}
						{isValidDate(eventEndDate) && dateFormatter(typeof eventEndDate === 'string' ? new Date(eventEndDate) : eventEndDate!, isRtl)}
					</time>
				</ChakraText>
			)}

			{description &&
					<Box noOfLines={4} mt={4} sx={{ ...ThemeTextStyling().sizes?.p16 }}>
						<sc.RichText editable={false} field={{ value: description }} />
					</Box>
			}
		</Stack>
	);
};

export default StoryListItemText;